import { forEach } from 'lodash';
import moment from 'moment';

import {
  LengthOfDiabetesDiagnosisEnum,
  TimeSinceLastEyeExamEnum,
  TypeOfDiabetesEnum,
} from './DiabetesData.model';
import { Disease } from './Disease.model';
import {
  DeviceDataView,
  ExamData,
  GradingDetail,
  GradingResults,
} from './GalenData/DeviceDataView.model';
import { OrderData } from './GalenData/Order.model';
import { Grading } from './Grading.model';
import { ReportStatus } from './ReportStatus.model';
import { ReportTemplate } from './ReportTemplate.model';
import { Result } from './Result.model';

export interface SessionBase {
  iid: string;
  username: string;

  id: string;
  firstName: string;
  lastName: string;
  age?: number;
  dob?: string;

  date: string; // YYYY-MM-DD
  time: string; // HH:mm:ss

  leftImageFile?: string;
  rightImageFile?: string;

  graded: boolean;

  leftResults?: Result[];
  rightResults?: Result[];

  createdAt: Date;

  completedTime?: Date;
  completedReason?: CompletedReasonEnum;

  template?: ReportTemplate;

  sessionId: string;
  practiceUserId: string;
}

export interface Session extends SessionBase {
  retakeSessions?: SessionBase[];
  order?: OrderData;
  orderId?: string;
  patientUserId?: string;
  typeOfDiabetes?: TypeOfDiabetesEnum;
  lengthOfDiabetesDiagnosis?: LengthOfDiabetesDiagnosisEnum;
  timeSinceLastEyeExam?: TimeSinceLastEyeExamEnum;
  siteName?: string;
  siteId?: string;
}

export enum CompletedReasonEnum {
  END_SESSION = 'End Session',
  COMPLETED_EXAM = 'Complete Exam',
  AUTO_COMPLETE = 'Auto Complete',
}

export enum ExamStatusEnum {
  IN_PROGRESS = 'In Progress',
  EXAM_ENDED = 'Exam Ended',
  EXAM_SUCCESSFUL = 'Exam Successful',
  UPLOADING = 'Uploading',
  UPLOAD_FAILED = 'Upload Failed',
  QUEUED = 'Queued',
  INCOMPLETE = 'Incomplete',
}

export enum EyesPosition {
  LEFT = 'left',
  RIGHT = 'right',
  BOTH = 'both',
}

export const transformExamToSession = (
  exam: DeviceDataView<ExamData>,
  username?: string,
) => {
  const patient = exam.owner;

  const leftEyeResults = exam?.data?.LeftEyeResultJson?.value.results;
  const rightEyeResults = exam?.data?.RightEyeResultJson?.value.results;

  const session: Session = {
    iid: exam.deviceDataId || '',
    username: username || '',

    id: patient?.patientId || '',
    firstName: patient?.firstName || '',
    lastName: patient?.lastName || '',
    patientUserId: patient?.userId || '',
    age: patient?.dateOfBirth
      ? moment(exam?.data?.ReportDatetime?.value).diff(
          patient.dateOfBirth,
          'years',
        )
      : undefined,
    dob: patient?.dateOfBirth,

    date: moment(exam?.data?.ReportDatetime?.value).format('YYYY-MM-DD'),
    time: moment(exam?.data?.ReportDatetime?.value).format('HH:mm:ss'),

    leftImageFile: exam?.data?.LeftImage?.link,
    rightImageFile: exam?.data?.RightImage?.link,

    graded:
      exam?.data?.Status?.value === ReportStatus.COMPLETE ||
      exam?.data?.Status?.value === ReportStatus.FAILED,

    leftResults:
      exam?.data.Status?.value === ReportStatus.COMPLETE
        ? resolveResult(leftEyeResults)
        : [],
    rightResults:
      exam?.data.Status?.value === ReportStatus.COMPLETE
        ? resolveResult(rightEyeResults)
        : [],

    createdAt: moment(exam?.data?.ExamSubmissionDatetime?.value).toDate(),

    completedTime: exam.data.CompletedTime?.value,
    completedReason: exam.data.CompletedReason?.value,

    orderId: exam?.data?.OrderId?.value,

    sessionId: exam?.data?.SessionId?.value,

    practiceUserId: exam.data.PracticeUserId?.value,

    siteId: exam?.data?.SiteId?.value,
    siteName: exam?.data?.SiteName?.value,

    typeOfDiabetes: exam?.data?.DiabetesType?.value,
    lengthOfDiabetesDiagnosis: exam?.data?.DurationOfDiabetes?.value,
    timeSinceLastEyeExam: exam?.data?.LastEyeExam?.value,
  };

  return session;
};

// Define a mapping of grading results to Result configurations
const gradingResultMapping: Record<
  string,
  { grading: Grading; gradable: boolean; error?: string }
> = {
  [Grading.UNGRADEABLE]: { grading: Grading.EMPTY, gradable: false },
  [Grading.ERROR]: {
    grading: Grading.EMPTY,
    gradable: false,
    error: 'Error',
  },
  [Grading.GOOD]: { grading: Grading.GOOD, gradable: true },
};

const resolveResult = (results: GradingResults): Result[] => {
  const resultList: Result[] = [];

  forEach(results, (value) => {
    const results = resolveTeleophResult(value);
    resultList.push(...results);
  });

  return resultList;
};

const resolveTeleophResult = (value: GradingDetail | undefined): Result[] => {
  if (!value) {
    return [];
  }

  const result = gradingResultMapping[value.grading.toLowerCase()];

  if (!result) {
    return [];
  }

  const { grading, gradable, error } = result;
  return [
    {
      disease: Disease.GENERAL_QUALITY,
      error,
      gradable,
      grading,
      conf: value.conf,
    },
  ];
};
